// @spec POL-STR-037
'use client';

import { createFileRoute } from '@tanstack/react-router';
import { FileText, Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  DeleteTemplateDialog,
  DuplicateTemplateDialog,
  TemplateFormDialog,
  TemplateList,
  useCategories,
  useCreateDocumentTemplate,
  useDeleteDocumentTemplate,
  useDocumentTemplates,
  useDuplicateDocumentTemplate,
  useFrameworks,
  useUpdateDocumentTemplate,
} from '@/features/policies';
import type {
  CreateDocumentTemplateInput,
  DocumentTemplate,
  UpdateDocumentTemplateInput,
} from '@/features/policies';
import { useOrgRole } from '@/hooks/use-org-role';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/admin/policies/templates')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.documentTemplates'),
  }),
  component: DocumentTemplatesPage,
});

/**
 * Admin page for managing document templates.
 *
 * Allows organization admins to:
 * - View all document templates
 * - Create new templates
 * - Edit existing templates
 * - Duplicate templates
 * - Delete templates
 */
function DocumentTemplatesPage() {
  const { t } = useTranslation('admin');
  const { isAdmin } = useOrgRole();

  // State for dialogs
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<DocumentTemplate | null>(null);
  const [deletingTemplate, setDeletingTemplate] = useState<DocumentTemplate | null>(null);
  const [duplicatingTemplate, setDuplicatingTemplate] = useState<DocumentTemplate | null>(null);

  // API hooks
  const { data: templatesData, isLoading: isLoadingTemplates } = useDocumentTemplates({
    includeInactive: true,
  });
  const { data: categories } = useCategories();
  const { data: frameworks } = useFrameworks();

  const createMutation = useCreateDocumentTemplate();
  const updateMutation = useUpdateDocumentTemplate();
  const deleteMutation = useDeleteDocumentTemplate();
  const duplicateMutation = useDuplicateDocumentTemplate();

  const templates = templatesData?.items ?? [];

  // Handlers
  const handleCreateOpen = () => {
    setIsCreateDialogOpen(true);
  };

  const handleEdit = (template: DocumentTemplate) => {
    setEditingTemplate(template);
  };

  const handleDelete = (template: DocumentTemplate) => {
    setDeletingTemplate(template);
  };

  const handleDuplicate = (template: DocumentTemplate) => {
    setDuplicatingTemplate(template);
  };

  const handleCreateSubmit = async (
    data: CreateDocumentTemplateInput | UpdateDocumentTemplateInput
  ) => {
    try {
      // For create, name is always present (required field in form)
      await createMutation.mutateAsync(data as CreateDocumentTemplateInput);
      toast.success(t('admin:toast.templateCreatedSuccessfully'));
      setIsCreateDialogOpen(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create template';
      toast.error(message);
      throw error;
    }
  };

  const handleUpdateSubmit = async (
    data: CreateDocumentTemplateInput | UpdateDocumentTemplateInput
  ) => {
    if (!editingTemplate) return;
    try {
      await updateMutation.mutateAsync({
        id: editingTemplate.id,
        data,
      });
      toast.success(t('admin:toast.templateUpdatedSuccessfully'));
      setEditingTemplate(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update template';
      toast.error(message);
      throw error;
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingTemplate) return;
    try {
      await deleteMutation.mutateAsync(deletingTemplate.id);
      toast.success(t('admin:toast.templateDeletedSuccessfully'));
      setDeletingTemplate(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete template';
      toast.error(message);
      throw error;
    }
  };

  const handleDuplicateConfirm = async (name: string) => {
    if (!duplicatingTemplate) return;
    try {
      await duplicateMutation.mutateAsync({
        id: duplicatingTemplate.id,
        data: { name },
      });
      toast.success(t('admin:toast.templateDuplicatedSuccessfully'));
      setDuplicatingTemplate(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to duplicate template';
      toast.error(message);
      throw error;
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
            <h2 className="text-lg font-medium text-destructive">{t('common:accessDenied.title')}</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {t('common:accessDenied.documentTemplatesDenied')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-4xl space-y-8">
        <header className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary-100 p-2 dark:bg-primary-900">
              <FileText className="h-6 w-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h1 className="text-3xl font-semibold text-foreground">Document Templates</h1>
              <p className="mt-1 text-muted-foreground">
                Create and manage reusable templates for policies, procedures, and standards.
              </p>
            </div>
          </div>

          <Button onClick={handleCreateOpen}>
            <Plus className="mr-2 h-4 w-4" />
            Add Template
          </Button>
        </header>

        {/* Stats summary */}
        {!isLoadingTemplates && templates.length > 0 && (
          <div className="flex gap-4 text-sm text-muted-foreground">
            <span>
              {templates.length} {templates.length === 1 ? 'template' : 'templates'}
            </span>
            <span>{templates.filter((t) => t.isActive).length} active</span>
            <span>{templates.filter((t) => !t.isActive).length} inactive</span>
          </div>
        )}

        {/* Templates list */}
        <TemplateList
          templates={templates}
          isLoading={isLoadingTemplates}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
        />

        {/* Create Dialog */}
        <TemplateFormDialog
          open={isCreateDialogOpen}
          onOpenChange={setIsCreateDialogOpen}
          categories={categories ?? []}
          frameworks={frameworks ?? []}
          onSubmit={handleCreateSubmit}
          isSubmitting={createMutation.isPending}
        />

        {/* Edit Dialog */}
        {editingTemplate && (
          <TemplateFormDialog
            open={!!editingTemplate}
            onOpenChange={(open) => {
              if (!open) setEditingTemplate(null);
            }}
            template={editingTemplate}
            categories={categories ?? []}
            frameworks={frameworks ?? []}
            onSubmit={handleUpdateSubmit}
            isSubmitting={updateMutation.isPending}
          />
        )}

        {/* Delete Dialog */}
        <DeleteTemplateDialog
          open={!!deletingTemplate}
          onOpenChange={(open) => {
            if (!open) setDeletingTemplate(null);
          }}
          template={deletingTemplate}
          onConfirm={handleDeleteConfirm}
          isDeleting={deleteMutation.isPending}
        />

        {/* Duplicate Dialog */}
        <DuplicateTemplateDialog
          open={!!duplicatingTemplate}
          onOpenChange={(open) => {
            if (!open) setDuplicatingTemplate(null);
          }}
          template={duplicatingTemplate}
          onConfirm={handleDuplicateConfirm}
          isDuplicating={duplicateMutation.isPending}
        />
      </div>
    </div>
  );
}
